import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiPost } from '../lib/api';
import useHaptics from '../hooks/useHaptics';
import './LogTimeSheet.css';

const PRESETS = [10, 15, 20, 30, 45, 60];

const pad = (n) => String(n).padStart(2, '0');
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const nowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Build an ISO string with local timezone offset from today's date + HH:MM
function buildStartedAt(hhmm) {
  const [hh, mm] = (hhmm || nowHHMM()).split(':').map(Number);
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  // toISOString returns UTC; we want a tz-aware ISO
  const tzMin = -d.getTimezoneOffset();
  const sign = tzMin >= 0 ? '+' : '-';
  const abs = Math.abs(tzMin);
  const tz = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${tz}`;
}

export default function LogTimeSheet({
  activity,
  open,
  onClose,
  onLogged,
  defaultMinutes,
  userId,
}) {
  const haptics = useHaptics();
  const planned = activity?.minutes ?? 0;
  const initialMinutes = defaultMinutes ?? planned ?? 30;

  const [selectedPreset, setSelectedPreset] = useState(() =>
    PRESETS.includes(initialMinutes) ? initialMinutes : null
  );
  const [customMinutes, setCustomMinutes] = useState(() =>
    PRESETS.includes(initialMinutes) ? '' : String(initialMinutes || '')
  );
  const [customMode, setCustomMode] = useState(() => !PRESETS.includes(initialMinutes));
  const [time, setTime] = useState(nowHHMM());
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const closeBtnRef = useRef(null);
  const submittingRef = useRef(false);
  const sheetRef = useRef(null);

  // Reset when sheet re-opens for a new activity.
  // Only depend on `open` and activity id so editing inside the sheet
  // doesn't reset state when the parent re-renders.
  useEffect(() => {
    if (!open) return;
    const init = defaultMinutes ?? activity?.minutes ?? 30;
    if (PRESETS.includes(init)) {
      setSelectedPreset(init);
      setCustomMinutes('');
      setCustomMode(false);
    } else {
      setSelectedPreset(null);
      setCustomMinutes(String(init || ''));
      setCustomMode(true);
    }
    setTime(nowHHMM());
    setErrorMsg('');
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  // ESC + focus management
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Keyboard avoidance for Android WebView — track visualViewport and expose
  // the keyboard height as a CSS custom property on the sheet.
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const apply = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      sheetRef.current?.style.setProperty('--lts-keyboard-inset', `${inset}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, [open]);

  const chosenMinutes = useMemo(() => {
    if (customMode) {
      const n = Number(customMinutes);
      return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
    }
    return selectedPreset || 0;
  }, [customMode, customMinutes, selectedPreset]);

  const overMaxLog = chosenMinutes > 1440;

  if (!open || !activity) return null;

  const submit = async (minutesOverride, withTime = true) => {
    if (submittingRef.current) return;
    const minutes = minutesOverride ?? chosenMinutes;
    if (!minutes || minutes <= 0) {
      setErrorMsg('Pick a duration first.');
      return;
    }
    if (minutes > 1440) {
      setErrorMsg('Max 24h per log.');
      return;
    }
    if (!userId || !activity?.id) {
      setErrorMsg("Couldn't save — try again.");
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    setErrorMsg('');
    try {
      const payload = {
        activity_id: activity.id,
        actual_minutes: minutes,
        date: todayKey(),
      };
      if (withTime) payload.started_at = buildStartedAt(time);
      const res = await apiPost(`/api/logs/${userId}`, payload);
      if (!res.ok) {
        setErrorMsg("Couldn't save — try again.");
        return;
      }
      let saved = null;
      try { saved = await res.json(); } catch { saved = null; }
      const logged = saved || { ...payload, activity_id: activity.id };
      haptics.trigger('light');
      onLogged?.(logged);
      // Notify other views (Today, Insights, Plans) that logs changed.
      try {
        window.dispatchEvent(
          new CustomEvent('hx:logs-changed', {
            detail: { activityId: activity.id, minutes },
          })
        );
      } catch (_) { /* non-DOM env */ }
      onClose?.();
    } catch {
      setErrorMsg("Couldn't save — try again.");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const handlePreset = (m) => {
    setCustomMode(false);
    setSelectedPreset(m);
    setCustomMinutes('');
  };

  const handleCustom = () => {
    setCustomMode(true);
    setSelectedPreset(null);
  };

  const handleMarkPlanned = () => submit(planned, true);

  return (
    <div className="lts-backdrop" onClick={onClose} role="presentation">
      <div
        ref={sheetRef}
        className="lts-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Log ${activity.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lts-handle" aria-hidden />
        <div className="lts-title">Log {activity.name}</div>

        <div className="lts-section">
          <span className="eyebrow lts-section-label">Minutes</span>
          <div className="lts-pills" role="radiogroup" aria-label="Duration in minutes">
            {PRESETS.map((m) => {
              const active = !customMode && selectedPreset === m;
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`lts-pill${active ? ' is-active' : ''}`}
                  onClick={() => handlePreset(m)}
                >
                  {m}
                </button>
              );
            })}
            <button
              type="button"
              role="radio"
              aria-checked={customMode}
              className={`lts-pill lts-pill-custom${customMode ? ' is-active' : ''}`}
              onClick={handleCustom}
            >
              Custom
            </button>
          </div>
          {customMode && (
            <input
              type="number"
              inputMode="numeric"
              min="1"
              className="lts-custom-input"
              placeholder="Minutes"
              value={customMinutes}
              onChange={(e) => setCustomMinutes(e.target.value.replace(/[^0-9]/g, ''))}
              autoFocus
            />
          )}
        </div>

        <div className="lts-section">
          <label htmlFor="lts-time" className="eyebrow lts-section-label">When?</label>
          <input
            id="lts-time"
            type="time"
            className="lts-time-input"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
          <span className="lts-hint">Defaults to now</span>
        </div>

        {errorMsg && (
          <div className="lts-error" role="alert">{errorMsg}</div>
        )}

        <button
          type="button"
          ref={closeBtnRef}
          className="btn-primary lts-cta"
          onClick={() => submit()}
          disabled={saving || !chosenMinutes || overMaxLog}
        >
          {saving ? 'Saving…' : 'Log it'}
        </button>
        <p className="lts-helper">You can log more later.</p>

        {planned > 0 && (
          <button
            type="button"
            className="lts-ghost"
            onClick={handleMarkPlanned}
            disabled={saving}
          >
            Mark as planned ({planned} min)
          </button>
        )}

        <button type="button" className="lts-cancel" onClick={onClose} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}
